import { z } from "zod"

import { avToolEingabeBasisSchema, avToolErgebnisSchema } from "./av-tool"
import { loesungVorschlagSchema } from "./loesung-produkte"
import {
  akquiseArtSchema,
  chatModeSchema,
  difficultySchema,
  feedbackImpulseSchema,
  feedbackSchema,
  feedbackStrengthSchema,
  gespraechsPhaseSchema,
  learningEssenceContentSchema,
  oneTimePasswordVariantSchema,
  personaFallSchema,
  personaObjectionsSchema,
  personaProfilSchema,
  playbookBausteinSchema,
  realtimeVoiceSchema,
  reflexionsStufeSchema,
  selbstbildSchema,
  tourChunkSchema,
  trainingsEinheitSchema,
} from "./schemas"

/**
 * Antwortformen der API.
 *
 * Diese Schemas sind die Quelle für zwei Dinge zugleich: die TypeScript-Typen,
 * mit denen das Backend arbeitet, und die OpenAPI-Spec, aus der der
 * Frontend-Client generiert wird. Was hier nicht steht, existiert für den
 * Client nicht.
 *
 * Konvention: Felder, die fehlen können, sind `.optional()`, nicht
 * `.nullable()`. Die Services wandeln Prisma-`null` konsequent in `undefined`
 * (siehe `toDto`), damit der generierte Client nicht überall `| null` bekommt.
 */

export const roleSchema = z.enum([
  "advisor",
  "coach",
  "manager",
  "platform",
  "content_admin",
])
export const userStatusSchema = z.enum(["invited", "active", "disabled"])
export const tenantStatusSchema = z.enum(["active", "paused"])

export const vertragsmodellSchema = z.enum([
  "pauschal",
  "je_lizenz",
  "je_aktivem_nutzer",
])
export type Vertragsmodell = z.infer<typeof vertragsmodellSchema>

export const auditLogKategorieSchema = z.enum(["verwaltung", "anmeldung"])
export type AuditLogKategorie = z.infer<typeof auditLogKategorieSchema>
export const onboardingStepSchema = z.enum([
  "selected",
  "setup",
  "onboarding",
  "live",
])
export const ssoProviderSchema = z.enum(["oidc", "saml", "azuread"])

export const sessionUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: roleSchema,
  roleLabel: z.string(),
  initials: z.string(),
  tenantId: z.string().optional(),
  teamId: z.string().optional(),
  status: userStatusSchema,
  mustChangePassword: z.boolean(),
  /** Null until the advisor picked a coach during onboarding. */
  coachStyleId: z.string().optional(),
  consentAt: z.string().optional(),
  /**
   * Tour chunks the advisor has finished or skipped. A chunk missing here
   * starts itself the next time its area is opened.
   */
  completedTours: z.array(tourChunkSchema),
  lastSeenAt: z.string().optional(),
})

export const authResponseSchema = z.object({ user: sessionUserSchema })

export const okResponseSchema = z.object({ ok: z.literal(true) })

export const healthResponseSchema = z.object({
  status: z.literal("ok"),
  time: z.string(),
})

export const personaSchema = z.object({
  isPersonal: z.boolean().optional(),
  id: z.string(),
  name: z.string(),
  age: z.number().int(),
  profession: z.string(),
  lifeSituation: z.string(),
  motive: z.string(),
  objections: personaObjectionsSchema,
  fall: personaFallSchema,
  profil: personaProfilSchema,
  communicationStyle: z.string(),
  difficulty: difficultySchema,
  status: z.enum(["draft", "active", "archived"]),
  voice: realtimeVoiceSchema.optional(),
  portraitUrl: z.string().optional(),
  tenantId: z.string().optional(),
})

export const coachStyleSchema = z.object({
  id: z.string(),
  name: z.string(),
  headline: z.string(),
  age: z.number().int(),
  portraitUrl: z.string().optional(),
  voice: realtimeVoiceSchema.optional(),
  voiceSampleUrl: z.string().optional(),
  strengths: z.array(z.string()),
  description: z.string(),
  tone: z.string(),
  directness: z.number().int().min(1).max(5),
  length: z.enum(["short", "medium", "detailed"]),
  status: z.enum(["active", "inactive"]),
  examplePhrase: z.string(),
  tenantId: z.string().optional(),
})

export const tenantSchema = z.object({
  id: z.string(),
  name: z.string(),
  region: z.string(),
  status: tenantStatusSchema,
  /** The platform's home tenant: listed, but never part of customer figures. */
  internal: z.boolean(),
  teams: z.number().int(),
  pilotTeams: z.number().int(),
  monthlyTrainings: z.number().int(),
  iddMinutes: z.number().int(),
  enabledModuleIds: z.array(z.string()),
  owner: z.string(),
  ssoProvider: ssoProviderSchema,
  ssoDomain: z.string(),
  ssoMetadataUrl: z.string(),
  ssoStatus: z.enum(["connected", "pending", "error"]),
  /**
   * Muster, über die sich Berater:innen dieser Sparkasse selbst anmelden
   * dürfen. Leer heißt: nur eingeladene Adressen kommen herein.
   */
  allowedEmailDomains: z.array(z.string()),
  /** Set by the platform; every run of this Sparkasse takes it over. */
  bausteine: playbookBausteinSchema.array(),
  branding: z.object({
    primaryColor: z.string(),
    logoUrl: z.string().optional(),
    welcomeText: z.string(),
  }),
  licenseSeats: z.number().int(),
  activeUsers: z.number().int(),
  // Aus Prisma-Decimal, im Service bereits nach Number konvertiert.
  monthlyFixedPriceEur: z.number(),
  perUserPriceEur: z.number(),
  vertragsmodell: vertragsmodellSchema,
  vertragBeginn: z.string().optional(),
  vertragEnde: z.string().optional(),
  abrechnungsKontakt: z.string(),
  onboardingStep: onboardingStepSchema,
  createdAt: z.string(),
})

export const teamSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  lead: z.string(),
  users: z.number().int(),
  focus: z.string(),
  /** Marks a pilot group. A label only: what a group may use comes from its releases. */
  pilot: z.boolean(),
  /** A paused group keeps its members, but its releases rest and the tenant's apply. */
  status: tenantStatusSchema,
  /** Optional term; outside it the group behaves like a paused one. */
  gueltigAb: z.string().optional(),
  gueltigBis: z.string().optional(),
})

export const tenantUserSchema = z.object({
  id: z.string(),
  tenantId: z.string().optional(),
  name: z.string(),
  email: z.string(),
  role: roleSchema,
  teamId: z.string().optional(),
  status: userStatusSchema,
  lastSeenAt: z.string().optional(),
  invitedAt: z.string().optional(),
  consentAt: z.string().optional(),
})

/**
 * Ein frisch vergebenes Einmal-Passwort. Wird genau einmal ausgeliefert und
 * nirgends gespeichert: in der Datenbank steht nur der Hash.
 */
export const oneTimePasswordSchema = z.object({
  email: z.string(),
  password: z.string(),
  variant: oneTimePasswordVariantSchema,
})

export const auditLogEntrySchema = z.object({
  id: z.string(),
  tenantId: z.string().optional(),
  at: z.string(),
  actor: z.string(),
  action: z.string(),
  detail: z.string().optional(),
  kategorie: auditLogKategorieSchema,
})

export const subprozessorSchema = z.object({
  id: z.string(),
  name: z.string(),
  purpose: z.string(),
  region: z.string(),
  avvStatus: z.enum(["signed", "pending", "review"]),
  dataCategories: z.array(z.string()),
})

/** One tool call kept with the coach answer for replay. */
export const toolCallRecordSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  input: z.unknown(),
  output: z.unknown(),
})

export const chatMessageResponseSchema = z.object({
  toolCalls: z.array(toolCallRecordSchema).optional(),
  id: z.string().optional(),
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  createdAt: z.string(),
  fromVoice: z.boolean().optional(),
  phase: gespraechsPhaseSchema.optional(),
  /**
   * Der abschließende Beitrag des Coaches im Reflexionsgespräch.
   *
   * Trägt die Feedback-Karte darunter: die Oberfläche zeigt sie an dieser
   * Nachricht, nicht am Ende des Verlaufs — nach dem Abschluss kann noch
   * weitergeschrieben werden.
   */
  istAbschluss: z.boolean().optional(),
})

/** One conversation with the coach, replayed when it is reopened. */
export const coachThreadResponseSchema = z.object({
  mode: chatModeSchema.optional(),
  produkt: z.string().optional(),
  /** Prüfung mode only: the focus area, so a resumed thread keeps it. */
  lernfeld: z.string().optional(),
  id: z.string(),
  /** Derived from the first question; absent until one was asked. */
  title: z.string().optional(),
  favorite: z.boolean(),
  updatedAt: z.string(),
  messages: z.array(
    chatMessageResponseSchema.extend({
      /** Which mode this turn was written in, so a mixed thread stays readable. */
      mode: chatModeSchema.optional(),
      toolCalls: z.array(toolCallRecordSchema).optional(),
      /** Who wrote this answer. Absent means the advisor's current coach. */
      coachStyleId: z.string().optional(),
    })
  ),
})

export const learningEssenceStatusSchema = z.enum([
  "open",
  "skipped",
  "completed",
])

/**
 * The advisor's own learning essence. Self-scoped: there is no id in the
 * response because there is no route to anyone else's. The raw answers stay
 * server-side on purpose — they only ground the onboarding prompt.
 */
export const learningEssenceResponseSchema = z.object({
  status: learningEssenceStatusSchema,
  threadId: z.string().optional(),
  essence: learningEssenceContentSchema.optional(),
  completedAt: z.string().optional(),
  skippedAt: z.string().optional(),
})

export const startLearningEssenceResponseSchema = z.object({
  threadId: z.string(),
})

/** Entry in the sidebar rail. Deliberately without messages: the rail lists. */
export const coachThreadSummarySchema = z.object({
  mode: chatModeSchema.optional(),
  produkt: z.string().optional(),
  /** Prüfung mode only: the focus area, so a resume link can carry it. */
  lernfeld: z.string().optional(),
  id: z.string(),
  title: z.string().optional(),
  favorite: z.boolean(),
  updatedAt: z.string(),
})

export const feedbackRatingSchema = z.object({
  id: z.string(),
  feedbackId: z.string(),
  userId: z.string(),
  score: z.number().int().min(1).max(5),
  comment: z.string().optional(),
  /** Welche Fassung der Bilanz bewertet wurde. */
  ratedRevision: z.number().int().optional(),
  createdAt: z.string(),
})

/**
 * Feedback in Antwortform.
 *
 * Wichtig: `dimensions`, `betterPhrases`, `practiceFocus` und
 * `iddClassification` liegen in Postgres als `Json`. Ohne dieses explizite
 * Schema würden sie im generierten Client als `unknown` landen und die
 * Feedback-Seite ihre Typisierung verlieren. Deshalb wird hier das ohnehin
 * vorhandene `feedbackSchema` wiederverwendet.
 */
export const feedbackResponseSchema = feedbackSchema.extend({
  id: z.string().optional(),
  rating: feedbackRatingSchema.optional(),
  // Optional in responses (unlike generation): feedback persisted before the
  // strengths/impulses redesign has neither block and must still serialize —
  // a required field here would drop every legacy row from the session list.
  strengths: z.array(feedbackStrengthSchema).optional(),
  impulses: z.array(feedbackImpulseSchema).optional(),
  suggestedQuestions: z.array(z.string()).optional(),
  /**
   * Woraus dieses Feedback entstanden ist.
   *
   * `dialog` ist das Ergebnis eines Reflexionsgesprächs und wird der Beraterin
   * als Bilanz gezeigt. `transkript` entsteht still beim Phasenabschluss ohne
   * Reflexion: es trägt Debrief und IDD-Nachweis, wird aber nicht angezeigt —
   * sonst wäre die fertige Auswertung durch die Hintertür zurück.
   *
   * Optional, weil es ein Persistenzmerkmal ist: ein im Speicher gebautes
   * Feedback (Attrappe ohne Modellzugang, Testdaten) hat keine Herkunft. Fehlt
   * das Feld, gilt das Feedback als nicht dialogisch entstanden — die Bilanz
   * wird dann nicht gezeigt, und das ist der sichere Ausgang.
   */
  herkunft: z.enum(["dialog", "transkript"]).optional(),
  /** Steigt, wenn die Bilanz nach einem längeren Gespräch nachgezogen wird. */
  revision: z.number().int().optional(),
})

/** Ein Stufenwechsel, wie er im Reflexionsgespräch protokolliert wird. */
export const reflectionTransitionSchema = z.object({
  von: reflexionsStufeSchema,
  nach: reflexionsStufeSchema,
  ausloeser: z.enum(["coach", "nutzer", "server"]),
  at: z.string(),
})

/**
 * Das Reflexionsgespräch zu einer abgeschlossenen Phase, in Antwortform.
 *
 * `selbstbild` reist mit, weil die Oberfläche zeigen muss, was schon erfasst
 * ist — aber ausschließlich auf der eigenen Sitzung. Es steht in keinem
 * Listen-DTO und in keinem Aggregat.
 */
export const reflectionSessionResponseSchema = z.object({
  stage: reflexionsStufeSchema,
  stageLog: z.array(reflectionTransitionSchema).default([]),
  selbstbild: selbstbildSchema.optional(),
  analyseFreigegebenAm: z.string().optional(),
  /** Eigene Beiträge im Reflexionsgespräch. Trägt die Freigabebedingung. */
  eigeneBeitraege: z.number().int().default(0),
  updatedAt: z.string(),
})
export type ReflectionSession = z.infer<typeof reflectionSessionResponseSchema>

export const trainingSessionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  tenantId: z.string().optional(),
  personaId: z.string(),
  coachStyleId: z.string(),
  mode: z.enum(["text", "voice", "mixed"]),
  messages: z.array(chatMessageResponseSchema),
  // The advisor's coaching conversation about this phase (text coach in the
  // review step). Personal learning content, only ever delivered on the
  // owning session — never in any aggregate.
  coachChat: z.array(chatMessageResponseSchema).default([]),
  activeSeconds: z.number().int(),
  idleSeconds: z.number().int(),
  startedAt: z.string(),
  endedAt: z.string().optional(),
  feedback: feedbackResponseSchema.optional(),
  /** Das Reflexionsgespräch zu dieser Phase. Fehlt bei Sitzungen von vor dem
   *  dialogischen Feedback — die Oberfläche zeigt dann die Archivansicht. */
  reflection: reflectionSessionResponseSchema.optional(),
  status: z.enum(["active", "completed"]),
  deletedAt: z.string().optional(),
  // Durchlauf-Integration
  processRunId: z.string().optional(),
  /** The trained unit. Missing for sessions from before the unit layer. */
  unit: trainingsEinheitSchema.optional(),
  /**
   * The unit's leading phase. Kept because playbook, rubrics and the evaluation
   * all work on the phase.
   */
  phase: gespraechsPhaseSchema.optional(),
})

const needsAnalysisFactSchema = z.object({
  value: z.number(),
  evidence: z.string(),
  source: z.enum(["tool", "reconciliation"]),
  capturedAt: z.string(),
})

/** Der geteilte Fallzustand, der die Etappen eines Durchlaufs verbindet. */
export const caseStateSchema = z.object({
  /**
   * Existing customer or cold call, picked before the call. Lives in the case
   * state and not on the persona, because the same customer can be either.
   */
  akquise: akquiseArtSchema.optional(),
  personaTruth: z
    .object({
      alter: z.number().int().optional(),
      familienstand: z.string().optional(),
      kinder: z.string().optional(),
      beruf: z.string().optional(),
      einkommenNetto: z.number().optional(),
      bestandsvorsorge: z.string().optional(),
      wohnsituation: z.string().optional(),
    })
    .optional(),
  /** Freie Persona-Konfiguration (überschreibt im Rollenspiel die Vorlage). */
  personaOverride: z
    .object({
      name: z.string().optional(),
      age: z.number().int().optional(),
      profession: z.string().optional(),
      lifeSituation: z.string().optional(),
      motive: z.string().optional(),
      objections: personaObjectionsSchema.optional(),
      communicationStyle: z.string().optional(),
      difficulty: difficultySchema.optional(),
    })
    .optional(),
  /**
   * Der Zahlensatz des Falls, beim Start aus der Persona übernommen. Was im
   * echten Gespräch im AV-Tool auf dem Bildschirm stünde.
   */
  fall: personaFallSchema.optional(),
  /** Automatisch aus den Gesprächen extrahierte Fakten (Checkpoint-Vorbefüllung). */
  gatheredInfo: z
    .array(z.object({ label: z.string(), value: z.string() }))
    .optional(),
  /**
   * Woran sich der KI-Kunde aus dem Durchlauf erinnert, neben den harten Fakten.
   *
   * Je Eintrag ein Satz aus seiner Sicht, nie ein Zitat und nie ein Verlauf:
   * das Gedächtnis reichert die folgenden Einheiten an, ohne das Modell mit dem
   * ganzen Vorgespräch zu belasten. `absprache` ist verbindlich („ihr habt
   * vereinbart"), `punkt` ist Erinnerung („darüber habt ihr gesprochen") — die
   * Unterscheidung entscheidet, wie der Prompt es formuliert, und welche
   * Einträge beim Deckel zuerst fallen.
   */
  gedaechtnis: z
    .array(
      z.object({
        art: z.enum(["absprache", "punkt"]),
        text: z.string(),
        /** Aus welcher Einheit der Eintrag stammt. */
        einheit: trainingsEinheitSchema,
      }),
    )
    .optional(),
  appointment: z
    .object({
      vereinbart: z.boolean(),
      /**
       * True only when the advisor played the appointment call herself.
       *
       * `vereinbart` alone cannot carry this: a run that starts at the needs
       * analysis pre-seeds it as settled. The red thread may only be demanded
       * from someone who actually set the occasion in this run.
       */
      selbstGefuehrt: z.boolean().optional(),
      hookGenutzt: z.string().optional(),
      dauer: z.string().optional(),
      /**
       * The slot the two actually settled on, as heard in the call.
       *
       * Beside `vereinbart` and not a sharpening of it: that flag is also set
       * by `preseedCaseState` for a run that skips the appointment call, and
       * the persona prompt turns it into "you already agreed an appointment".
       * This field carries only what the advisor worked out herself, and it is
       * the one the worksheet counts — otherwise the sheet would show an
       * appointment nobody ever made.
       */
      termin: z
        .object({
          /** The day as agreed: "Dienstag, 12.05." */
          tag: z.string(),
          /** The time as agreed: "16:00 Uhr" */
          uhrzeit: z.string(),
          evidence: z.string(),
          capturedAt: z.string(),
        })
        .optional(),
    })
    .optional(),
  agenda: z
    .object({
      thema: z.string().optional(),
      erwartungen: z.array(z.string()).optional(),
      zugestimmt: z.boolean().optional(),
    })
    .optional(),
  needs: z
    .object({
      rentenwunsch: z.number().optional(),
      nettoAnker: z.number().optional(),
    })
    .optional(),
  gap: z
    .object({
      luecke: z.number().optional(),
      mitInflation: z.number().optional(),
    })
    .optional(),
  /** Values the AI explicitly recorded from the needs-analysis conversation. */
  needsAnalysis: z
    .object({
      nettoEinkommen: needsAnalysisFactSchema,
      grvAnwartschaft: needsAnalysisFactSchema,
      privateAnwartschaft: needsAnalysisFactSchema,
      jahreBisRente: needsAnalysisFactSchema,
      wunschrente: needsAnalysisFactSchema,
      luecke: needsAnalysisFactSchema,
      lueckeMitInflation: needsAnalysisFactSchema,
    })
    .partial()
    .optional(),
  /**
   * Was die Beraterin im AV-Tool gerechnet hat.
   *
   * Bewusst NEBEN und nicht in `needsAnalysis`: dort steht, was im Gespräch
   * gesagt wurde — der KI-Sidecar trägt nur Genanntes ein und weist alles ab,
   * was nicht zum Trainingsfall passt. Hier steht, was die Beraterin getippt
   * und das Werkzeug gerechnet hat. Beides darf auseinanderlaufen; genau die
   * Abweichung ist der Lerngegenstand im Feedback. Wer hier schreibt, schreibt
   * nicht dort.
   *
   * `gezeigtAm` ist die einzige Brücke zum KI-Kunden: erst wenn die Beraterin
   * das Ergebnis bewusst zeigt (im echten Termin dreht sie den Schirm um),
   * wandert die Lücke nach `gap` und damit in den Rollenspiel-Prompt.
   *
   * `.catch(undefined)`: dieselbe Falle wie bei `personaFallSchema`. Ein Stand
   * aus einer älteren Feld- oder Annahmenversion darf nicht den gesamten
   * Fallzustand mitreißen.
   */
  avTool: z
    .object({
      /**
       * Die Basis OHNE `superRefine`: eine später verschärfte Querprüfung darf
       * einen gespeicherten Stand nicht rückwirkend ungültig machen.
       */
      eingabe: avToolEingabeBasisSchema,
      ergebnis: avToolErgebnisSchema,
      /** Mit welchem Annahmensatz gerechnet wurde. */
      annahmenStand: z.string(),
      annahmenVersion: z.number().int(),
      aktualisiertAm: z.string(),
      gezeigtAm: z.string().optional(),
    })
    .optional()
    .catch(undefined),
  /**
   * The products the advisor selected on screen in "Lösung & Abschluss".
   *
   * Beside `solution` on purpose, like `avTool` beside `needsAnalysis`:
   * `solution` holds what was said in the conversation, this what was clicked.
   * Only the input is stored; the pensions are recalculated from it.
   */
  loesung: z
    .object({
      vorschlaege: z.array(loesungVorschlagSchema),
      aktualisiertAm: z.string(),
      gezeigtAm: z.string().optional(),
    })
    .optional()
    .catch(undefined),
  solution: z
    .object({
      beitrag: z.number().optional(),
      schmerzgrenze: z.number().optional(),
      variante: z.enum(["klassisch", "investiert"]).optional(),
    })
    .optional(),
  objections: z
    .object({
      raised: z.array(z.string()),
      handled: z.array(z.string()),
    })
    .optional(),
  preparation: z
    .object({
      checklist: z.array(
        z.object({
          key: z.string(),
          checked: z.boolean(),
          label: z.string().optional(),
          value: z.string().optional(),
        })
      ),
      goal: z.string(),
    })
    .optional(),
  /**
   * The follow-up work: one answer per leading question, keyed by
   * `NACHBEREITUNG_FRAGEN`. Its presence unlocks the second step of the
   * follow-up unit — only then is the debrief generated.
   */
  nachbereitung: z
    .object({
      antworten: z.array(z.object({ key: z.string(), value: z.string() })),
    })
    .optional(),
})
export type CaseState = z.infer<typeof caseStateSchema>

/** Ein Prozess-Durchlauf mit seinen Etappen-Sessions und dem Fallzustand. */
export const processRunSchema = z.object({
  id: z.string(),
  userId: z.string(),
  tenantId: z.string().optional(),
  personaId: z.string(),
  coachStyleId: z.string(),
  status: z.enum(["active", "completed", "abandoned"]),
  /** The training plan: the chosen units in canonical order. */
  units: trainingsEinheitSchema.array(),
  /** Die gewählten Bausteine. Leer heißt abgewählt, nicht ungesetzt. */
  bausteine: playbookBausteinSchema.array(),
  currentUnit: trainingsEinheitSchema.optional(),
  // The phases of the chosen units, written out. They stay in the response
  // because playbook, case briefing and debrief all work on the phase.
  phases: gespraechsPhaseSchema.array(),
  currentPhase: gespraechsPhaseSchema.optional(),
  caseState: caseStateSchema,
  startedAt: z.string(),
  completedAt: z.string().optional(),
  sessions: z.array(trainingSessionSchema),
  // The advisor's coaching conversation about the whole Durchlauf (run-wide
  // coach below the debrief). Personal learning content, only ever delivered
  // on the owning run — never in any aggregate.
  coachChat: z.array(chatMessageResponseSchema).default([]),
})

/**
 * Kompakte Listenansicht eines Durchlaufs für die Historie. Bewusst ohne die
 * Etappen-Sessions: die volle Form (`processRunSchema`) trägt Transkripte,
 * Feedback und Coach-Chats aller Phasen — viel zu schwer für eine Liste.
 */
export const processRunSummarySchema = z.object({
  /** Most recent persisted activity, including stage sessions and coaching. */
  lastActivityAt: z.string().optional(),
  id: z.string(),
  personaId: z.string(),
  status: z.enum(["active", "completed", "abandoned"]),
  units: trainingsEinheitSchema.array(),
  currentUnit: trainingsEinheitSchema.optional(),
  phases: gespraechsPhaseSchema.array(),
  currentPhase: gespraechsPhaseSchema.optional(),
  startedAt: z.string(),
  completedAt: z.string().optional(),
  /** Summe der aktiven Sekunden über alle Etappen-Sessions (IDD). */
  activeSeconds: z.number().int(),
  /** Ø über die Phasen-Feedbacks (Dimensionsschnitt je Phase), falls vorhanden. */
  averageScore: z.number().min(1).max(5).optional(),
  /** How many units already have a feedback. */
  feedbackCount: z.number().int(),
})

/** The summative overall debrief across all units. */
export const debriefSchema = z.object({
  runId: z.string(),
  summary: z.string(),
  redThread: z.string(),
  strongestStage: z.string(),
  biggestLever: z.string(),
  overallFocus: z.string(),
  unitScores: z.array(
    z.object({
      unit: trainingsEinheitSchema,
      label: z.string(),
      score: z.number().min(1).max(5).optional(),
    })
  ),
  /**
   * Competencies aggregated across *all* units: per dimension the average over
   * the units in which it was scored. Weakest first.
   */
  dimensionAverages: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      average: z.number().min(1).max(5),
      samples: z.number().int(),
    })
  ),
  /** The evidenced highlights per unit, pulled from their feedbacks. */
  unitBreakdown: z.array(
    z.object({
      unit: trainingsEinheitSchema,
      label: z.string(),
      score: z.number().min(1).max(5).optional(),
      /**
       * The unit in three sentences, written with its own feedback. Absent for
       * feedback from before this field existed; the fields below then carry
       * the card on their own.
       */
      zusammenfassung: z
        .object({
          staerke: z.string(),
          hebel: z.string(),
          transfer: z.string(),
        })
        .optional(),
      summary: z.string().optional(),
      strongestMoment: z.string().optional(),
      biggestRisk: z.string().optional(),
      practiceFocus: z.string().optional(),
    })
  ),
  /**
   * Klickbare Schnellfragen für den Coach-Chat unter dem Debrief. Optional:
   * Debriefs, die vor diesem Feature erzeugt wurden, haben das Feld nicht.
   */
  suggestedQuestions: z.array(z.string()).optional(),
})

// --- Auswertung ---

export const usageSummarySchema = z.object({
  since: z.string(),
  totalCostEur: z.number(),
  totalAudioMinutes: z.number().int(),
  events: z.number().int(),
  byKind: z.array(
    z.object({
      kind: z.enum(["text", "realtime", "tts", "stt"]),
      events: z.number().int(),
      costEur: z.number(),
      inputTokens: z.number().int(),
      outputTokens: z.number().int(),
      audioMinutes: z.number().int(),
    })
  ),
})

export const analyticsOverviewSchema = z.object({
  periodDays: z.number().int(),
  since: z.string(),
  sessions: z.object({
    total: z.number().int(),
    completed: z.number().int(),
    activeUsers: z.number().int(),
    seats: z.number().int(),
  }),
  idd: z.object({
    totalMinutes: z.number().int(),
    averageMinutesPerSession: z.number().int(),
  }),
  /** Die Akzeptanzzahl, auf die es beim Go/No-Go ankommt. */
  acceptance: z.object({
    averageScore: z.number(),
    ratings: z.number().int(),
    distribution: z.array(
      z.object({ score: z.number().int(), count: z.number().int() })
    ),
  }),
  /** Welche Phasen am häufigsten trainiert werden (ersetzt die Produktverteilung). */
  topPhases: z.array(
    z.object({
      phase: gespraechsPhaseSchema,
      label: z.string(),
      sessions: z.number().int(),
    })
  ),
  cost: usageSummarySchema,
})

export const trainingsPerDaySchema = z.array(
  z.object({
    day: z.string(),
    sessions: z.number().int(),
    minutes: z.number().int(),
  })
)

export const dimensionAveragesSchema = z.array(
  z.object({
    key: z.string(),
    label: z.string(),
    average: z.number(),
    samples: z.number().int(),
  })
)

export const transcribeResponseSchema = z.object({ text: z.string() })

// --- Abgeleitete Typen. Ersetzen die frueheren handgeschriebenen Typen. ---

export type SessionUser = z.infer<typeof sessionUserSchema>
export type Persona = z.infer<typeof personaSchema>
export type CoachStyle = z.infer<typeof coachStyleSchema>
export type Tenant = z.infer<typeof tenantSchema>
export type Team = z.infer<typeof teamSchema>
export type TenantUser = z.infer<typeof tenantUserSchema>
export type OneTimePassword = z.infer<typeof oneTimePasswordSchema>
export type AuditLogEntry = z.infer<typeof auditLogEntrySchema>
export type Subprozessor = z.infer<typeof subprozessorSchema>
export type ChatMessage = z.infer<typeof chatMessageResponseSchema>
export type CoachThreadResponse = z.infer<typeof coachThreadResponseSchema>
export type ToolCallRecord = z.infer<typeof toolCallRecordSchema>
export type CoachThreadSummary = z.infer<typeof coachThreadSummarySchema>
export type Feedback = z.infer<typeof feedbackResponseSchema>
export type FeedbackRating = z.infer<typeof feedbackRatingSchema>
export type TrainingSession = z.infer<typeof trainingSessionSchema>
export type ProcessRun = z.infer<typeof processRunSchema>
export type ProcessRunSummary = z.infer<typeof processRunSummarySchema>
export type Debrief = z.infer<typeof debriefSchema>
export type AnalyticsOverview = z.infer<typeof analyticsOverviewSchema>
export type UsageSummary = z.infer<typeof usageSummarySchema>
